import { dispatch } from 'nike-re-framejs';
import { fromJS, Map, OrderedMap, Set } from 'immutable';

import { deepMerge } from 'nike-ui/src/common/immutable/merge';
import array2map from 'nike-ui/src/common/immutable/array2map';
import notificationClient from '@/_http/axiosNotificationClient';
import ticketusClient from '@/_http/axiosTicketusClient';
import { getContext, storeSettings } from './web-notification-service-ctx';
import {
    init,
    loadMatchSubscriptionTypes as loadMatchSubscriptionTypesApi,
    loadSubscriptions as loadSubscriptionsApi,
    loadTicketSubscriptions as loadTicketSubscriptionsApi,
    subscribeToMatch as subscribeToMatchApi,
    subscribeToTicket as subscribeToTicketApi,
    unsubscribeFromTicket as unsubscribeFromTicketApi,
    updateCustomerSubscription as updateCustomerSubscriptionApi
} from 'nike-ui/src/components/notifications/nike-notification-server-api';

init('MOBILE', notificationClient, getContext);

export const updateCustomerSubscription = (type, value) => db => {
    updateCustomerSubscriptionApi(type, value).catch(console.log);
    return db.updateIn(['notifications', 'customer'], customer => (value ? customer.add(type) : customer.delete(type)));
};

export function loadSubscriptions(db) {
    if (!db.get('loggedIn')) return db;
    loadSubscriptionsApi()
        .catch(() => ({
            betbookSubscriptions: [],
            customerSubscriptions: [],
            matchSubscriptions: [],
            ticketSubscriptions: []
        }))
        .then(data =>
            dispatch(db =>
                db
                    .setIn(['notifications', 'betbook'], betbookSubscriptionsMap(data.betbookSubscriptions))
                    .updateIn(['notifications', 'customer'], customer =>
                        deepMerge(customer, Set(data.customerSubscriptions || []))
                    )
                    .updateIn(['notifications', 'match'], match =>
                        deepMerge(match, array2map(data.matchSubscriptions || [], 'eventId'))
                    )
                    .setIn(['notifications', 'tickets'], ticketNotificationsMap(data.ticketSubscriptions))
            )
        );
    return db;
}

const betbookSubscriptionsMap = data =>
    Map(
        (data || []).map(b => {
            const { analysesTracking, blogsTracking, ticketsTracking } = b;
            return [b.authorNickname, Map({ analysesTracking, blogsTracking, ticketsTracking })];
        })
    );

const ticketNotificationsMap = data =>
    Map(
        (data || []).map(t => {
            const { ticketTracking, picksTracking } = t;
            return [String(t.ticketId), Map({ ticketTracking, picksTracking })];
        })
    );

export function loadMinimalTicketNotifications(db) {
    loadTicketSubscriptionsImpl()
        .then(data => data.map(t => t.ticketId))
        .then(ticket_ids => {
            if (!ticket_ids || !ticket_ids.length) {
                return;
            }
            ticketusClient
                .get('tickets/minimal', {
                    params: { ticket_ids }
                })
                .then(fromJS)
                .then(tdata => tdata.reduce((acc, ticket) => acc.set(ticket.get('ticketId'), ticket), OrderedMap()))
                .then(data => dispatch(db => db.setIn(['ticketus', 'minimal'], data)));
        })
        .catch(console.log);
    return db;
}

export const storeNotificationsSettings = (ticketTracking, picksTracking) => db => {
    storeSettings({ ticketTracking, picksTracking });
    return db
        .setIn(['notifications', 'settings', 'ticketTracking'], ticketTracking)
        .setIn(['notifications', 'settings', 'picksTracking'], picksTracking);
};

export function loadTicketSubscriptions(db) {
    if (db.get('loggedIn')) {
        loadTicketSubscriptionsImpl().catch(console.log);
    }
    return db;
}

function loadTicketSubscriptionsImpl() {
    return loadTicketSubscriptionsApi()
        .then(data => {
            dispatch(db => db.setIn(['notifications', 'tickets'], ticketNotificationsMap(data)));
            return data;
        })
        .catch(console.log);
}

export const subscribeToMatch = (eventId, types) => db => {
    // optimistic UI
    subscribeToMatchApi(eventId, types.toJS()).catch(console.log);
    return db.updateIn(['notifications', 'match', eventId, 'types'], prev => deepMerge(prev, Set(types)));
};

export const subscribeToTicket = (ticketId, ticketTracking, picksTracking) => db => {
    subscribeToTicketApi(ticketId, ticketTracking || false, picksTracking || false).catch(console.log);
    return db.setIn(['notifications', 'tickets', ticketId], fromJS({ ticketTracking, picksTracking }));
};

export const unsubscribeFromTicket = ticketId => db => {
    unsubscribeFromTicketApi(ticketId).catch(console.log);
    return db.updateIn(['notifications', 'tickets'], ticketIds => ticketIds.delete(ticketId));
};

export const loadMatchNotificationTypes = eventId => db => {
    if (!db.getIn(['notifications', 'types', 'match', eventId])) {
        loadMatchSubscriptionTypesApi(eventId)
            .then(fromJS)
            .then(data => dispatch(db => db.setIn(['notifications', 'types', 'match', eventId.toString()], data)));
    }
    return db;
};
